import React, {useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";
import {FormattedMessage} from "react-intl";

import {makeStyles} from "@mui/styles";
import {AddPhotoAlternateOutlined, Delete} from "@mui/icons-material";

import {darkGrayColor, infoColor} from "../../../assets/jss/main";

const useStyles = makeStyles(() => ({
    label: {
        color: infoColor,
        display: "block",
        marginBottom: "5px",
    },
    dropzone: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        cursor: "pointer",
        borderRadius: "15px",
        border: "2px dashed #E0E0E0",
        color: darkGrayColor,
        fontSize: "12px",
        padding: "20px",
    },
    preview: {
        position: "relative",
        display: "inline-block",
        borderRadius: "15px",
        border: "2px dashed #E0E0E0",
        padding: "10px",
    },
    image: {
        maxWidth: "150px",
        maxHeight: "80px",
        display: "block",
    },
    remove: {
        position: "absolute",
        right: "5px",
        top: "5px",
        color: darkGrayColor,
        cursor: "pointer",
        backgroundColor: "#FFF",
        borderRadius: "35px",
    },
}));

/**
 * Upload field for a tag (fanion) image: shows a dropzone when empty,
 * a preview with a delete action otherwise.
 *
 * `image` is either the URL of an already stored image, a File pending
 * upload, or null when no image is set.
 */
export default function TagImageDropzone({ label, image, onDrop, onDelete }) {
    const classes = useStyles();
    const preview = useMemo(
        () => (image instanceof File ? URL.createObjectURL(image) : image),
        [image]
    );

    useEffect(() => () => {
        if (image instanceof File && preview) {
            URL.revokeObjectURL(preview);
        }
    }, [image, preview]);

    return (
        <div>
            <span className={classes.label}>{label}</span>
            {preview ? (
                <div className={classes.preview}>
                    <Delete className={classes.remove} onClick={onDelete}/>
                    <img className={classes.image} src={preview} alt={label}/>
                </div>
            ) : (
                <Dropzone accept="image/*" multiple={false} onDrop={(droppedFiles) => onDrop(droppedFiles[0])}>
                    {({getRootProps, getInputProps}) => (
                        <div {...getRootProps()} className={classes.dropzone}>
                            <input {...getInputProps()} />
                            <AddPhotoAlternateOutlined/>
                            <FormattedMessage id={"product.form.action.upload"}/>
                        </div>
                    )}
                </Dropzone>
            )}
        </div>
    );
}

TagImageDropzone.propTypes = {
    label: PropTypes.string.isRequired,
    image: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),
    onDrop: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};
